import ChartLoading from '../../../components/ChartLoading/ChartLoading';
import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup } from '@attain-sre/attain-design-system';
import { GET_TTD_OTS_SEGMENT_METRICS } from '../../../graphql/queries';
import { NIVO_COMMON, NIVO_COMMON_LINE } from '../utils/nivoCommon';
import {
  OTS_SGM_MNG_ACTIVE_IDS_LABEL,
  OTS_SGM_MNG_CHART_DATA_NOT_AVAILABLE,
  OTS_SGM_MNG_CPM_LABEL,
  OTS_SGM_MNG_EFF_CPM_LABEL,
  OTS_SGM_MNG_IMPRESSIONS_LABEL,
  OTS_SGM_MNG_MONTHLY,
  OTS_SGM_MNG_QUARTERLY,
  OTS_SGM_MNG_RECEIVED_IDS_LABEL,
  OTS_SGM_MNG_REVENUE_LABEL,
  OTS_SGM_MNG_SEGMENT_SIZE_REVENUE,
  OTS_SGM_MNG_SEGMENT_SIZE_REVENUE_SUBTITLE,
  OTS_SGM_MNG_SEGMENT_SIZE_REVENUE_SUBTITLE_QUARTERLY,
} from '../../../content';
import { PeriodData, SegmentSizeRevenueResponse } from '../types';
import { ResponsiveLine } from '@nivo/line';
import { formatCurrency, formatNumber } from '../utils/utils';
import { useQuery } from '@apollo/client';
import * as Styled from './index.styles';

interface OtsSegmentSizeRevenueChartProps {
  segmentId: string;
}

type PeriodType = 'monthly' | 'quarterly';

export const OtsSegmentSizeRevenueChart: React.FC<
  OtsSegmentSizeRevenueChartProps
> = ({ segmentId }) => {
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');

  const { data, loading, error } = useQuery<{
    ttdOtsSegmentMetrics: SegmentSizeRevenueResponse;
  }>(GET_TTD_OTS_SEGMENT_METRICS, {
    variables: {
      segmentId,
      periodType,
      month: undefined, // Optional: defaults to current month in backend
    },
    fetchPolicy: 'network-only',
    skip: !segmentId,
  });

  const chartData = data?.ttdOtsSegmentMetrics;

  // Transform data for Nivo chart with normalized scales
  const { lineData, scaleInfo } = useMemo(() => {
    if (!chartData || !chartData.periods || chartData.periods.length === 0) {
      return { lineData: [], scaleInfo: null };
    }

    // STEP 1: Create a map of period -> data for quick lookup
    const dataMap = new Map<string, PeriodData>();
    chartData.data.forEach((d) => {
      dataMap.set(d.period, d);
    });

    // STEP 2: Build chart data using ALL periods (labels)
    // Fill in null for missing data points
    const activeIdsData = chartData.periods.map((period) => {
      const dataPoint = dataMap.get(period);
      return {
        x: period,
        y: dataPoint?.activeIds.value ?? null, // ← null if no data
      };
    });

    const revenueData = chartData.periods.map((period) => {
      const dataPoint = dataMap.get(period);
      return {
        x: period,
        y: dataPoint?.revenue.value ?? null,
      };
    });

    // STEP 3: Calculate scales only from available data (not null)
    const activeIdsValues = activeIdsData
      .filter((d) => d.y !== null)
      .map((d) => d.y as number);
    const revenueValues = revenueData
      .filter((d) => d.y !== null)
      .map((d) => d.y as number);

    // Show chart if at least one series has data
    if (activeIdsValues.length === 0 && revenueValues.length === 0) {
      return { lineData: [], scaleInfo: null };
    }

    // Handle case where one series might be all nulls
    const hasActiveIdsData = activeIdsValues.length > 0;
    const hasRevenueData = revenueValues.length > 0;

    const maxActiveIds = hasActiveIdsData ? Math.max(...activeIdsValues) : 100;
    const minActiveIds = hasActiveIdsData ? Math.min(...activeIdsValues) : 0;
    const maxRevenue = hasRevenueData ? Math.max(...revenueValues) : 100;
    const minRevenue = hasRevenueData ? Math.min(...revenueValues) : 0;

    // Calculate scale ranges
    const activeIdsRange = maxActiveIds - minActiveIds || 1;
    const revenueRange = maxRevenue - minRevenue || 1;

    // Normalize revenue to active IDs scale for display
    const normalizedRevenueData = revenueData.map((d) => {
      if (d.y === null) return { x: d.x, y: null, originalValue: null };

      const normalizedValue =
        minActiveIds + ((d.y - minRevenue) / revenueRange) * activeIdsRange;
      return {
        x: d.x,
        y: normalizedValue,
        originalValue: d.y,
      };
    });

    // Colors matching the reference (blue and orange)
    const activeIdsColor = 'rgb(80, 50, 140)'; // Blue
    const revenueColor = 'rgb(255, 140, 0)'; // Orange

    return {
      lineData: [
        {
          id: OTS_SGM_MNG_ACTIVE_IDS_LABEL,
          color: activeIdsColor,
          data: activeIdsData,
        },
        {
          id: OTS_SGM_MNG_REVENUE_LABEL,
          color: revenueColor,
          data: normalizedRevenueData,
        },
      ],
      scaleInfo: {
        activeIdsMin: minActiveIds,
        activeIdsMax: maxActiveIds,
        revenueMin: minRevenue,
        revenueMax: maxRevenue,
        activeIdsColor,
        revenueColor,
        hasActiveIdsData,
        hasRevenueData,
      },
    };
  }, [chartData]);

  // Calculate Y-axis scale (using Active IDs scale since revenue is normalized)
  const yScaleConfig = useMemo(() => {
    if (!scaleInfo) {
      return { type: 'linear' as const, min: 0, max: 100 };
    }

    return {
      type: 'linear' as const,
      min: Math.max(0, scaleInfo.activeIdsMin * 0.9),
      max: scaleInfo.activeIdsMax * 1.1,
    };
  }, [scaleInfo]);

  // Calculate tick values for left axis (5 ticks)
  const leftAxisTickValues = useMemo(() => {
    if (!scaleInfo) return undefined;
    const min = Math.max(0, scaleInfo.activeIdsMin * 0.9);
    const max = scaleInfo.activeIdsMax * 1.1;
    const range = max - min;
    const step = range / 4; // 5 ticks = 4 intervals
    // Round to clean integers to avoid floating point precision errors
    return [
      Math.round(min),
      Math.round(min + step),
      Math.round(min + step * 2),
      Math.round(min + step * 3),
      Math.round(max),
    ];
  }, [scaleInfo]);

  // Calculate tick values for right axis (5 ticks) - based on revenue scale, converted to normalized scale
  const rightAxisTickValues = useMemo(() => {
    if (!scaleInfo) return undefined;
    // Calculate revenue tick values (5 ticks)
    const revenueMin = scaleInfo.revenueMin;
    const revenueMax = scaleInfo.revenueMax;
    const revenueRange = revenueMax - revenueMin || 1;
    const revenueStep = revenueRange / 4; // 5 ticks = 4 intervals
    const revenueTicks = [
      revenueMin,
      revenueMin + revenueStep,
      revenueMin + revenueStep * 2,
      revenueMin + revenueStep * 3,
      revenueMax,
    ];

    // Convert revenue tick values to normalized scale (Active IDs scale)
    const activeIdsMin = Math.max(0, scaleInfo.activeIdsMin * 0.9);
    const activeIdsMax = scaleInfo.activeIdsMax * 1.1;
    const normalizedRange = activeIdsMax - activeIdsMin || 1;

    return revenueTicks.map((revenueValue) => {
      // Normalize revenue value to Active IDs scale
      const normalizedValue =
        activeIdsMin +
        ((revenueValue - revenueMin) / revenueRange) * normalizedRange;
      // Round to avoid floating point precision errors
      return Math.round(normalizedValue * 100) / 100;
    });
  }, [scaleInfo]);

  // Format tooltip content - uses period label to lookup data
  const formatTooltipContent = (periodIndex: number) => {
    if (!chartData || !chartData.periods || !chartData.periods[periodIndex]) {
      return null;
    }

    const period = chartData.periods[periodIndex];

    // Find data by period label
    const periodData = chartData.data.find((d) => d.period === period);

    if (!periodData) {
      // No data for this period
      return (
        <Styled.TooltipContent>
          <Styled.TooltipTitle>{period}</Styled.TooltipTitle>
          <Styled.TooltipRow>
            <Styled.TooltipValue style={{ color: '#999', fontStyle: 'italic' }}>
              No data available
            </Styled.TooltipValue>
          </Styled.TooltipRow>
        </Styled.TooltipContent>
      );
    }

    return (
      <Styled.TooltipContent>
        <Styled.TooltipTitle>{period}</Styled.TooltipTitle>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>
            {OTS_SGM_MNG_ACTIVE_IDS_LABEL}:
          </Styled.TooltipLabel>
          <Styled.TooltipValue>
            {periodData.activeIds.label}
          </Styled.TooltipValue>
        </Styled.TooltipRow>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>
            {OTS_SGM_MNG_RECEIVED_IDS_LABEL}:
          </Styled.TooltipLabel>
          <Styled.TooltipValue>
            {periodData.receivedIds.label}
          </Styled.TooltipValue>
        </Styled.TooltipRow>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>
            {OTS_SGM_MNG_REVENUE_LABEL}:
          </Styled.TooltipLabel>
          <Styled.TooltipValue>{periodData.revenue.label}</Styled.TooltipValue>
        </Styled.TooltipRow>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>
            {OTS_SGM_MNG_IMPRESSIONS_LABEL}:
          </Styled.TooltipLabel>
          <Styled.TooltipValue>
            {periodData.impressions.label}
          </Styled.TooltipValue>
        </Styled.TooltipRow>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>{OTS_SGM_MNG_CPM_LABEL}:</Styled.TooltipLabel>
          <Styled.TooltipValue>{periodData.cpm.label}</Styled.TooltipValue>
        </Styled.TooltipRow>
        <Styled.TooltipRow>
          <Styled.TooltipLabel>
            {OTS_SGM_MNG_EFF_CPM_LABEL}:
          </Styled.TooltipLabel>
          <Styled.TooltipValue>{periodData.ecpm.label}</Styled.TooltipValue>
        </Styled.TooltipRow>
      </Styled.TooltipContent>
    );
  };

  if (error) {
    return null;
  }

  return (
    <Styled.CardContainer>
      <Styled.HeaderContainer>
        <Styled.TitleContainer>
          <Styled.Title>{OTS_SGM_MNG_SEGMENT_SIZE_REVENUE}</Styled.Title>
          <Styled.Subtitle>
            {periodType === 'monthly'
              ? OTS_SGM_MNG_SEGMENT_SIZE_REVENUE_SUBTITLE
              : OTS_SGM_MNG_SEGMENT_SIZE_REVENUE_SUBTITLE_QUARTERLY}
          </Styled.Subtitle>
        </Styled.TitleContainer>
        <ButtonGroup>
          <Button
            onClick={() => setPeriodType('monthly')}
            variant={periodType === 'monthly' ? 'contained' : 'outlined'}
          >
            {OTS_SGM_MNG_MONTHLY}
          </Button>
          <Button
            onClick={() => setPeriodType('quarterly')}
            variant={periodType === 'quarterly' ? 'contained' : 'outlined'}
          >
            {OTS_SGM_MNG_QUARTERLY}
          </Button>
        </ButtonGroup>
      </Styled.HeaderContainer>

      <Styled.ContentWrapper>
        {loading ? (
          <Styled.ChartContainer>
            <ChartLoading barOrientation="vertical" chartType="bar" />
          </Styled.ChartContainer>
        ) : chartData && lineData.length > 0 ? (
          <Styled.ChartContainer>
            <div style={{ height: '300px' }}>
              <ResponsiveLine
                {...NIVO_COMMON_LINE}
                animate={true}
                data={lineData}
                margin={{ top: 13, right: 100, bottom: 31, left: 75 }}
                xScale={{ type: 'point' }}
                yScale={yScaleConfig}
                defs={[
                  {
                    id: 'gradientActiveIds',
                    type: 'linearGradient',
                    colors: [
                      {
                        offset: 0,
                        color: scaleInfo?.activeIdsColor || 'rgb(80, 50, 140)',
                        opacity: 1,
                      },
                      {
                        offset: 10,
                        color: scaleInfo?.activeIdsColor || 'rgb(80, 50, 140)',
                        opacity: 0,
                      },
                    ],
                  },
                  {
                    id: 'gradientRevenue',
                    type: 'linearGradient',
                    colors: [
                      {
                        offset: 0,
                        color: scaleInfo?.revenueColor || 'rgb(255, 140, 0)',
                        opacity: 1,
                      },
                      {
                        offset: 10,
                        color: scaleInfo?.revenueColor || 'rgb(255, 140, 0)',
                        opacity: 0,
                      },
                    ],
                  },
                ]}
                fill={[
                  {
                    match: { id: OTS_SGM_MNG_ACTIVE_IDS_LABEL },
                    id: 'gradientActiveIds',
                  },
                  {
                    match: { id: OTS_SGM_MNG_REVENUE_LABEL },
                    id: 'gradientRevenue',
                  },
                ]}
                axisTop={null}
                axisRight={{
                  ...NIVO_COMMON.axes,
                  format: (value) => {
                    // Convert normalized value back to revenue scale for display
                    if (!scaleInfo) return '';

                    // If revenue has no data, show empty
                    if (!scaleInfo.hasRevenueData) return '';

                    const activeIdsMin = Math.max(
                      0,
                      scaleInfo.activeIdsMin * 0.9
                    );
                    const activeIdsMax = scaleInfo.activeIdsMax * 1.1;
                    const normalizedRange = activeIdsMax - activeIdsMin || 1;
                    const revenueRange =
                      scaleInfo.revenueMax - scaleInfo.revenueMin || 1;
                    const revenueValue =
                      scaleInfo.revenueMin +
                      ((value - activeIdsMin) / normalizedRange) * revenueRange;

                    return formatCurrency(revenueValue);
                  },
                  legend: OTS_SGM_MNG_REVENUE_LABEL,
                  legendOffset: 65,
                  tickValues: rightAxisTickValues,
                }}
                axisLeft={{
                  ...NIVO_COMMON.axes,
                  format: (value) => formatNumber(value),
                  legend: OTS_SGM_MNG_ACTIVE_IDS_LABEL,
                  legendOffset: -69,
                  tickValues: leftAxisTickValues,
                }}
                axisBottom={{
                  ...NIVO_COMMON.axes,
                }}
                enableGridY={false}
                enableSlices="x"
                useMesh={true}
                colors={(d) => d.color}
                sliceTooltip={({ slice }) => {
                  const periodIndex = chartData.periods.indexOf(
                    String(slice.points[0].data.x)
                  );
                  return formatTooltipContent(periodIndex);
                }}
              />
            </div>

            <Styled.LegendContainer>
              <Styled.LegendItem>
                <Styled.LegendDot color="rgb(80, 50, 140)" />
                <Styled.LegendLabel>
                  {OTS_SGM_MNG_ACTIVE_IDS_LABEL}
                </Styled.LegendLabel>
              </Styled.LegendItem>
              <Styled.LegendItem>
                <Styled.LegendDot color="rgb(255, 140, 0)" />
                <Styled.LegendLabel>
                  {OTS_SGM_MNG_REVENUE_LABEL}
                </Styled.LegendLabel>
              </Styled.LegendItem>
            </Styled.LegendContainer>
          </Styled.ChartContainer>
        ) : (
          <Styled.NoDataContainer>
            <Styled.NoDataText>
              {OTS_SGM_MNG_CHART_DATA_NOT_AVAILABLE}
            </Styled.NoDataText>
          </Styled.NoDataContainer>
        )}
      </Styled.ContentWrapper>
    </Styled.CardContainer>
  );
};

export default OtsSegmentSizeRevenueChart;
